import { useEffect, useState, useCallback, useRef } from 'react';

export interface UseIdleTimeoutOptions {
  /** Timeout duration in milliseconds before considering user idle */
  timeoutMs: number;
  /** Callback function when user becomes idle */
  onIdle?: () => void;
  /** Events to listen for user activity */
  events?: string[];
  /** Whether the idle timer is enabled */
  enabled?: boolean;
}

/**
 * Custom hook to detect user inactivity
 * @param options Configuration options for idle timeout
 * @returns Object with idle state and reset function
 */
export function useIdleTimeout(options: UseIdleTimeoutOptions) {
  const {
    timeoutMs,
    onIdle,
    events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ],
    enabled = true,
  } = options;

  const [isIdle, setIsIdle] = useState(false);
  const timeoutIdRef = useRef<number | null>(null);

  const resetTimer = useCallback(() => {
    // Clear existing timeout
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    // Reset idle state
    setIsIdle(false);

    // Set new timeout
    if (enabled) {
      timeoutIdRef.current = setTimeout(() => {
        setIsIdle(true);
        if (onIdle) {
          onIdle();
        }
      }, timeoutMs);
    }
  }, [enabled, timeoutMs, onIdle]);

  useEffect(() => {
    if (!enabled) {
      setIsIdle(false);
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      return;
    }

    // Initialize timer
    resetTimer();

    // Add event listeners for user activity
    events.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    // Cleanup
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [enabled, events, resetTimer]);

  return {
    isIdle,
    resetTimer,
  };
}
